<template>
  <div>
    <div class="header-actions">
      <PaginationSimple
        :page="page"
        :per-page="perPage"
        :options="perPageOptions"
        :disabled="loading"
        :next-page-available="!!apiNavigation.next"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
      />
      <DatePicker :date="date" @change="handleDateChange" />
    </div>

    <div v-if="loaded && posts.length">
      <div class="list-cols">
        <div>{{ $t("message.posts.multimedia") }}</div>
        <div>{{ $t("message.posts.postName") }}</div>
        <div>
          {{ $t("message.common.date") }}
        </div>
      </div>
      <v-list>
        <v-list-item-group active-class="">
          <v-virtual-scroll
            class="list"
            :items="posts"
            :item-height="listItemHeight"
            height="calc(50vh - 120px)"
            ref="list"
          >
            <template v-slot:default="{ item }">
              <v-radio-group
                v-model="selectedItem"
                hide-details
                class="radio-group"
              >
                <v-list-item
                  :key="item.id"
                  :value="item.id"
                  :ripple="false"
                  class="list-item"
                  @click="selectedItem = item.id"
                >
                  <template v-slot:default>
                    <v-list-item-action class="list-item__action">
                      <v-radio :value="item.id" hide-details></v-radio>
                    </v-list-item-action>
                    <v-list-item-content>
                      <div class="list-item__content">
                        <div class="list-item__image">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                class="list-item__external-link"
                                color="dark"
                                :href="item.permalink_url"
                                target="_blank"
                                fab
                                small
                                v-bind="attrs"
                                v-on="on"
                                @click.stop
                              >
                                <v-icon>mdi-open-in-new</v-icon>
                              </v-btn>
                            </template>
                            <span>{{
                              $t("message.posts.checkOriginalPost")
                            }}</span>
                          </v-tooltip>
                          <img :src="item.full_picture" />
                        </div>
                        <div class="list-item__data">
                          <div>
                            <div class="list-item__description">
                              <span>
                                {{ limitText(item.message) }}
                              </span>
                              <v-tooltip
                                v-if="isTextTruncated(item.message)"
                                max-width="500"
                                bottom
                                content-class="tooltip-opacity"
                              >
                                <template v-slot:activator="{ on, attrs }">
                                  <span
                                    v-bind="attrs"
                                    v-on="on"
                                    class="list-item__more"
                                    >{{ $t("message.common.readMore") }}</span
                                  >
                                </template>
                                <span>{{ item.message }}</span>
                              </v-tooltip>
                            </div>
                            <div v-if="item.is_used" class="list-item__info">
                              <v-icon color="accent" size="17.5">
                                mdi-information
                              </v-icon>
                              {{ $t("message.posts.usedForGoogleMapsPost") }}
                            </div>
                          </div>
                          <div class="list-item__date">
                            {{ item.created_time | formatDate }}
                          </div>
                        </div>
                      </div>
                    </v-list-item-content>
                  </template>
                </v-list-item>
              </v-radio-group>
            </template>
          </v-virtual-scroll>
        </v-list-item-group>
      </v-list>

      <SectionLoader :status="loading" :opacity="0.75" />
    </div>
    <div v-else class="no-data">
      <p class="mb-0">
        {{ $t("message.common.noData") }}
      </p>
      <SectionLoader :status="loading" :opacity="1" />
    </div>

    <div class="action-bar">
      <ui-button outlined @click="handleStepBack">
        {{ $t("message.common.back") }}
      </ui-button>
      <PaginationSimple
        :page="page"
        :per-page="perPage"
        :options="perPageOptions"
        :disabled="loading"
        :next-page-available="!!apiNavigation.next"
        :show-per-page="false"
        center
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
      />
      <ui-button
        color="primary"
        :disabled="!selectedItem"
        @click="handleNextStep"
      >
        {{ $t("message.common.further") }}
      </ui-button>
    </div>
  </div>
</template>

<script>
import PaginationSimple from "@/components/List/PaginationSimple";
import SectionLoader from "@/components/Loader/SectionLoader";
import { scrollToTop } from "@/helpers/helpers";
import serviceHandler from "@/helpers/handlers/service";
import facebookService from "@/services/facebook";
import DatePicker from "@/components/Posts/FbDatePicker.vue";
import moment from "moment";
import { FORMAT } from "@/config/date-format";
import { PERIOD } from "@/enums/period.enum";

export default {
  name: "FbPostsDialogView",
  components: {
    PaginationSimple,
    SectionLoader,
    DatePicker
  },
  filters: {
    formatDate(date) {
      return moment(date).format(FORMAT.DATE);
    }
  },
  props: {
    pageId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loaded: false,
      loading: false,
      totalCount: 0,
      page: 1,
      perPageOptions: [10, 25, 50, 100],
      perPage: 10,
      posts: [],
      listItemHeight: 180,
      selectedItem: null,
      apiNavigation: {
        next: null,
        previous: null
      },
      date: [
        moment()
          .subtract(3, PERIOD.MONTH)
          .add(1, PERIOD.DAY)
          .format(FORMAT.DATE),
        moment().format(FORMAT.DATE)
      ],
      payload: {}
    };
  },
  async created() {
    await this.initializeData();
  },
  methods: {
    handleStepBack() {
      this.$emit("back");
    },
    deletePagingPayload() {
      delete this.payload.next_page;
      delete this.payload.previous_page;
    },
    clearPagination() {
      this.page = 1;
      this.deletePagingPayload();
    },
    updateNavigationPayload(newPage) {
      if (newPage > this.page && this.apiNavigation.next) {
        delete this.payload.previous_page;
        this.payload.next_page = this.apiNavigation.next;
      } else if (this.apiNavigation.previous) {
        delete this.payload.next_page;
        this.payload.previous_page = this.apiNavigation.previous;
      }
    },
    updatePageSize(pageSize) {
      this.payload.page_size = pageSize;
      this.perPage = pageSize;
    },
    updateDatePayload(date) {
      this.payload.date_from = date[0];
      this.payload.date_to = date[1];
    },
    async handleDateChange(e) {
      this.handleListUpdate(() => {
        this.updateDatePayload(e);
      });
    },
    handlePageChange(page) {
      this.handleListUpdate(() => {
        this.updateNavigationPayload(page);
        this.page = page;
      });
    },
    handlePerPageChange(perPage) {
      this.handleListUpdate(() => {
        this.clearPagination();
        this.updatePageSize(perPage);
      });
    },

    async handleListUpdate(callback) {
      this.$nextTick(() => {
        scrollToTop(this.$refs.list.$el);
      });
      callback();
      await this.fetchPosts();
      if (!this.posts.length) {
        this.clearPagination();
        this.setApiNavigation(null, null);
        await this.fetchPosts();
      }
    },
    isTextTruncated(text) {
      return text && text.length > 300;
    },
    limitText(text) {
      if (!text) return "";
      return text.length > 300 ? text.slice(0, 300) + "..." : text;
    },
    handleNextStep() {
      const selectedItem = this.posts.find(
        post => post.id === this.selectedItem
      );
      this.$emit("submit", selectedItem);
    },

    async initializeData() {
      this.payload.facebook_page_id = this.pageId;
      this.updateDatePayload(this.date);
      this.updateNavigationPayload(this.page);
      this.updatePageSize(this.perPage);

      await this.fetchPosts();
      this.loaded = true;
    },
    setApiNavigation(next, previous) {
      this.apiNavigation.next = next;
      this.apiNavigation.previous = previous;
    },
    async fetchPosts() {
      this.loading = true;
      const { results, next, previous } = await serviceHandler(
        facebookService.getPosts(this.payload)
      );

      this.setApiNavigation(next, previous);
      this.posts = results;
      this.loading = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.v-tooltip__content {
  background: rgba(darken($primary, 30%), 0.95) !important;
}

.list-cols {
  display: flex;
  @include font-size(12px);
  color: $text;
  margin-bottom: $spacing;

  & > div:first-child {
    width: 230px;
    padding-left: 55px;
    flex-shrink: 0;
  }

  & > div:nth-child(2) {
    width: 100%;
  }

  & > div:last-child {
    flex-shrink: 0;
    width: 145px;
  }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: $spacing;
}

::v-deep {
  .v-list {
    padding: 0;
  }

  .v-input--radio-group__input {
    height: 180px;
  }

  .radio-group {
    padding-top: 0;
    margin-top: 0;
  }
}

.list {
  border-top: 1px solid $light-3;
  position: relative;
}

.list-item {
  border-bottom: 1px solid $light-3;
  height: 180px !important;

  &__more {
    font-weight: 500;
    color: $primary;
  }

  &__data {
    display: flex;
    align-items: center;
  }

  &__date {
    color: $primary;
    font-weight: 500;
    padding: 0 $spacing * 4;
    flex-shrink: 0;
  }

  &__content {
    display: flex;
    flex-direction: row;
    gap: $spacing * 2;
    align-items: center;
    @include font-size(14px);
  }

  &__image {
    width: 156px;
    height: 164px;
    border-radius: 20px;
    overflow: hidden;
    flex-shrink: 0;
    position: relative;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__external-link {
    position: absolute;
    top: $spacing;
    right: $spacing;
    z-index: 1;
    width: 30px;
    height: 30px;

    ::v-deep .v-icon {
      @include font-size(18px);
      color: $primary;
    }
  }

  &__action {
    margin-right: $spacing * 2 !important;
  }

  &__name,
  &__description {
    color: $dark;
  }

  &__description {
    line-height: 1.4em;
  }

  &__name {
    color: $text;
    font-weight: 500;
    margin-top: $spacing * 0.5;
    margin-bottom: 0;
  }

  &__info {
    margin-top: $spacing * 2;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: $spacing;
  }
}

.action-bar {
  display: flex;
  justify-content: space-between;
  flex-grow: 1;
  padding: 24px 0;
  gap: $spacing * 2;
}

.no-data {
  @include noData(100px);
}
</style>
